import {useMemo} from '@wordpress/element';
import {registerExpressPaymentMethod} from '@woocommerce/blocks-registry';
import {getSettings, initStripe as loadStripe, canMakePayment, isCartPage, isCheckoutPage} from "../util";
import {useExpressBreakpointWidth, useProcessExpressCheckoutError} from '../hooks';
import {Elements, PaymentRequestButtonElement, useStripe} from "@stripe/react-stripe-js";
import {
    usePaymentRequest,
    useProcessPaymentIntent,
    useExportedValues,
    useAfterProcessingPayment,
    useStripeError
} from '../hooks';
import {PaymentRequestPreview} from "./preview";

const getData = getSettings('stripe_payment_request_data');

const PaymentRequestContent = (props) => {
    return (
        <div className='wc-stripe-payment-request-container'>
            <Elements stripe={loadStripe} options={getData('elementOptions')}>
                <PaymentRequestButton {...props}/>
            </Elements>
        </div>
    );
}

const PaymentRequestButton = (
    {
        getData,
        onClick,
        onClose,
        onError,
        billing,
        shippingData,
        eventRegistration,
        emitResponse,
        onSubmit,
        activePaymentMethod,
        ...props
    }) => {
    const {onPaymentSetup, onCheckoutFail} = eventRegistration;
    const {noticeContexts} = emitResponse;
    const stripe = useStripe();
    const [error] = useStripeError();
    const canPay = (result) => result != null && !result.applePay && result.googlePay;
    const exportedValues = useExportedValues();

    useExpressBreakpointWidth({payment_method: getData('name'), width: 375});

    const {setPaymentMethod} = useProcessPaymentIntent({
        getData,
        billing,
        shippingData,
        onPaymentSetup,
        emitResponse,
        error,
        onSubmit,
        activePaymentMethod,
        exportedValues
    });

    useProcessExpressCheckoutError({
        onError,
        emitResponse,
        onCheckoutFail,
        paymentMethodId: getData('name'),
        activePaymentMethod
    });

    useAfterProcessingPayment({
        getData,
        eventRegistration,
        emitResponse,
        activePaymentMethod,
        messageContext: noticeContexts.EXPRESS_PAYMENTS
    });

    const {paymentRequest} = usePaymentRequest({
        getData,
        onClose,
        stripe,
        billing,
        shippingData,
        eventRegistration,
        setPaymentMethod,
        exportedValues,
        canPay
    });

    const options = useMemo(() => {
        return {
            paymentRequest,
            style: {
                paymentRequestButton: getData('paymentRequestButton')
            }
        }
    }, [paymentRequest]);

    if (paymentRequest) {
        return (
            <PaymentRequestButtonElement options={options} onClick={onClick}/>
        )
    }
    return null;
}

registerExpressPaymentMethod({
    name: getData('name'),
    canMakePayment: ({cartTotals}) => {
        const {currency_code: currency, total_price} = cartTotals;
        if (isCartPage() && !getData('cartCheckoutEnabled')) {
            return false;
        }
        if (isCheckoutPage() && !getData('expressCheckoutEnabled')) {
            return false;
        }
        return canMakePayment({
            country: getData('countryCode'),
            currency: currency.toLowerCase(),
            total: {
                label: getData('totalLabel'),
                amount: parseInt(total_price)
            }
        }, (result) => result != null && !result.applePay && result.googlePay);
    },
    content: <PaymentRequestContent getData={getData}/>,
    edit: <PaymentRequestPreview getData={getData}/>,
    supports: {
        showSavedCards: getData('showSavedCards'),
        showSaveOption: getData('showSaveOption'),
        features: getData('features')
    }
});